import { ref, computed } from 'vue'
import axios from 'axios'
import orderTemplateService from '@/services/orderTemplateService'
import productCatalogService from '@/services/productCatalogService'
import { filterProductsBySearch, sortGroupsByMatch } from '@/utilities/productSearchRank'

const RECOMMENDS_GROUP_KEY = 'rh-recommends'
const RECOMMENDS_GROUP_ID = 'rh_recommends'
const RECOMMENDS_GROUP_NAME = 'Roof Hub Recommends / Recently Ordered'

/**
 * Shared product quick-search logic, extracted from the order template / estimate
 * "item description" search so the global search bar can reuse the exact same
 * behavior (debounced search, loading state, stale response cancellation and
 * results grouped by category with Roof Hub Recommends first).
 */
export function useProductQuickSearch() {
    const searchTerm = ref('')
    const isLoading = ref(false)
    const suggestions = ref({})
    const recommendations = ref([])
    const frequentlyPurchasedItems = ref([])
    const nextSearchIndex = ref(0)

    let currentRequestId = 0
    let cancelTokenSource = null

    const productCount = computed(() => {
        return Object.keys(searchResults.value).reduce((counts, key) => {
            const group = searchResults.value[key]
            if (group?.category?.id) {
                counts[group.category.id] = (group.items || []).length
            }
            return counts
        }, {})
    })

    const searchResults = computed(() => {
        let recommends = [...recommendations.value]
        let frequent = [...frequentlyPurchasedItems.value]

        // Do not repeat an item that is already in Roof Hub Recommends.
        frequent = frequent.filter(
            item => !recommends.some(rec => (rec.ProductId ?? rec.productId) === (item.ProductId ?? item.productId))
        )

        // Filter recommendations by the search term so only actual matches are shown.
        if (searchTerm.value && searchTerm.value.length > 0) {
            recommends = filterProductsBySearch(recommends, searchTerm.value)
            frequent = filterProductsBySearch(frequent, searchTerm.value)
        }

        const recommendsGroup = {
            category: {
                id: RECOMMENDS_GROUP_ID,
                name: RECOMMENDS_GROUP_NAME
            },
            items: [...frequent, ...recommends]
        }

        let results = {}

        // When searching, show the closest matching category groups first,
        // then Roof Hub Recommends. Otherwise recommendations lead.
        if (searchTerm.value && searchTerm.value.length > 0) {
            const sortedGroups = sortGroupsByMatch(
                Object.keys(suggestions.value).map(key => ({ ...suggestions.value[key], key })),
                searchTerm.value
            )
            sortedGroups.forEach(group => { results[group.key] = group })
            results[RECOMMENDS_GROUP_KEY] = recommendsGroup
        } else {
            results = {
                [RECOMMENDS_GROUP_KEY]: recommendsGroup,
                ...suggestions.value
            }
        }

        // Never render an empty category header.
        return Object.keys(results).reduce((kept, key) => {
            if ((results[key].items || []).length > 0) {
                kept[key] = results[key]
            }
            return kept
        }, {})
    })

    /** Flat, ordered list of results used for keyboard navigation and empty checks. */
    const orderedResults = computed(() =>
        Object.keys(searchResults.value).reduce(
            (all, key) => [...all, ...(searchResults.value[key].items || [])],
            []
        )
    )

    const hasResults = computed(() => orderedResults.value.length > 0)

    async function loadRecommendations(branchCode) {
        try {
            const response = await productCatalogService.getProductRecommendations(branchCode || '', 0)
            if (response?.success && response?.data) {
                recommendations.value = response.data?.RoofhubRecommends || []
                frequentlyPurchasedItems.value = response.data?.FrequentlyOrdered || []
            } else {
                recommendations.value = []
                frequentlyPurchasedItems.value = []
            }
        } catch (error) {
            recommendations.value = []
            frequentlyPurchasedItems.value = []
            console.error('Failed to load recommendations', { error })
        }
    }

    function getCategory(product) {
        const categories = product?.categories || product?.Categories || []
        const category = categories[0] || {}
        const id = category.categoryId ?? category.CategoryId
        const name = category.categoryName ?? category.CategoryName
        return {
            id: id != null ? id.toString() : 'uncategorized',
            name: name || 'Other'
        }
    }

    /** Same branch availability rule the estimate search uses. */
    function isAvailableAtBranch(item, branchCode) {
        if (!branchCode) return true
        if (item.availableAtBranch !== undefined) return item.availableAtBranch
        if (item.variants?.length > 0) return item.variants.some(v => v.availableAtBranch)
        return true
    }

    function normalizeProduct(product) {
        // Sort variants alphabetically, then push the branch-available ones up,
        // and fall back to the first variant image when the product has none.
        const variants = [...(product.variants || [])]
            .sort((a, b) => {
                const textA = a.colorName?.toUpperCase()
                const textB = b.colorName?.toUpperCase()
                return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
            })
            .sort((a, b) => {
                if (a.availableAtBranch) return 1
                if (b.availableAtBranch) return -1
                return 0
            })

        return {
            ...product,
            variants,
            availableAtBranch: variants.some(v => v.availableAtBranch),
            imageUrl: product.imageUrl ?? (variants.find(v => !!v.imageUrl)?.imageUrl ?? null)
        }
    }

    async function getSuggestions({ branchCode = '', excludeNotAvailableAtBranch = true, skip = 0 } = {}) {
        const requestId = ++currentRequestId

        if (!skip || skip <= 0) {
            nextSearchIndex.value = 0
            suggestions.value = {}
        }

        const term = searchTerm.value || ''
        const category = term === '' ? 'ALL' : ''

        if (cancelTokenSource) {
            cancelTokenSource.cancel('Previous search canceled')
        }
        cancelTokenSource = axios.CancelToken.source()

        isLoading.value = true

        try {
            const response = await orderTemplateService.productSearch(
                term,
                category,
                branchCode,
                excludeNotAvailableAtBranch,
                skip || 0,
                null, // selectedManufacturers
                false, // includeFrequentItems
                true, // includePromotions
                false, // includeRecommendations
                false, // recommendationsOnly
                null,
                [],
                cancelTokenSource.token
            )

            // Drop stale responses so the dropdown always reflects the latest input.
            if (requestId !== currentRequestId) {
                return
            }

            if (response?.success && response?.data && response.data.ErrorCode === 0) {
                nextSearchIndex.value = response?.data?.Data?.NextProductSetStartIndex || 0

                // Recommendations are only surfaced when the user is browsing, not searching.
                if (!term && (skip || 0) === 0) {
                    await loadRecommendations(branchCode)
                } else if ((skip || 0) === 0) {
                    recommendations.value = []
                    frequentlyPurchasedItems.value = []
                }

                const products = (response.data.Data?.products || [])
                    .map(normalizeProduct)
                    .filter(p => isAvailableAtBranch(p, branchCode))

                suggestions.value = products.reduce((previous, current) => {
                    const category = getCategory(current)
                    if (!previous[category.name]) {
                        previous[category.name] = {
                            category,
                            items: [current],
                            availableAtBranch: current.availableAtBranch
                        }
                    } else {
                        previous[category.name].items.push(current)
                    }
                    return previous
                }, { ...(skip > 0 ? suggestions.value : {}) })
            }
        } catch (error) {
            if (axios.isCancel?.(error) || error?.__CANCEL__) {
                return
            }
            console.error(`Failure getting search data: ${error?.message}`)
        } finally {
            if (requestId === currentRequestId) {
                isLoading.value = false
            }
        }
    }

    function reset() {
        currentRequestId++
        if (cancelTokenSource) {
            cancelTokenSource.cancel('Search cleared')
            cancelTokenSource = null
        }
        searchTerm.value = ''
        suggestions.value = {}
        nextSearchIndex.value = 0
        isLoading.value = false
    }

    return {
        searchTerm,
        isLoading,
        searchResults,
        orderedResults,
        hasResults,
        productCount,
        nextSearchIndex,
        loadRecommendations,
        getSuggestions,
        reset
    }
}
